/**
 * Build-time feature switches shared by the client and the server, so both
 * sides of a feature turn off together and cannot drift.
 *
 * Every switch below hides a feature; none deletes one. Flipping a value and
 * redeploying brings the feature back with its code, routes and data intact.
 * docs/v2-core/SWITCHES.md lists each switch, what it controls and how to flip it.
 */

/**
 * The institutional (multi-tenant) layer: organizations — hospitals, schools —
 * with member roles, org-owned patients, and the medical-proxy intake links.
 *
 * Temporarily OFF so the app presents as family-only. Flipping this back to
 * `true` restores it: no data is deleted, no migration is reversed, and the
 * org tables keep their rows (org-owned children are simply invisible while
 * this is false, because `actorFromSession` reports no org context).
 *
 * The explicit `: boolean` annotation is deliberate — it keeps the type wide
 * so TypeScript does not narrow the disabled branches to `never`, which would
 * fill the editor with unreachable-code noise in whichever state is off.
 */
export const INSTITUTIONS_ENABLED: boolean = false;

/** Every v2 feature, by its folder name under client/src/features/. */
export type V2FeatureId =
  | "welcome"
  | "community"
  | "safeSpace"
  | "stories"
  | "registry"
  | "seasonal"
  | "dailyWins"
  | "shop"
  | "partners"
  | "directory";

/**
 * The v2 product scope agreed with Ford on the Oct 20 call: secure care-profile
 * sharing plus a warm parent community. The server mounts a feature's routes
 * only while its switch is on (a switched-off feature's endpoints answer the
 * standard 404 envelope), and the client drops its routes, nav items, slots
 * and links. Typed `Record<…, boolean>` for the same reason as above.
 */
export const V2_FEATURES: Record<V2FeatureId, boolean> = {
  welcome: true,
  community: true,
  safeSpace: true,
  stories: true,
  registry: false, // Off since the Oct 22 call: "we don't have to do that now"
  seasonal: true, // PH market only: never shown in the main market
  dailyWins: false, // "Maybe"
  shop: false, // Later
  partners: false, // Later
  directory: false, // Later
};

/**
 * Child photos and community post photos. OFF: nobody can upload one, and the
 * server never returns `photoUrl` (child responses and the public share
 * payload alike). Existing photos stay in the database untouched, and
 * removing a photo is always allowed.
 */
export const PHOTO_UPLOADS_ENABLED: boolean = false;

/**
 * The health reminders: medication-expiry and allergy-plan-gap alerts. OFF: the
 * scheduler and scripts/run-reminders.ts skip both jobs, the notification feed
 * and unread count leave those kinds out, and the dashboard stops counting
 * expiring medications. Existing notification rows stay in the database.
 */
export const HEALTH_REMINDERS_ENABLED: boolean = false;

/**
 * Pickup & consent: the authorized-pickup list and the consent-to-treat +
 * insurance record. OFF: the child profile has no Pickup & Consent tab, the
 * share dialog no longer offers the `pickup` section, the authorized-pickups
 * and care-authorizations routes are not mounted (the standard 404 envelope),
 * and neither the care link nor the Emergency Information Form carries them,
 * even for a link issued with `pickup: true`. Existing rows stay in the
 * database untouched; flipping this back to `true` restores every surface.
 */
export const PICKUP_CONSENT_ENABLED: boolean = false;

/**
 * The first-sign-in welcome popup (the welcome sheet opening by itself on a
 * new account's first visit, region question first). OFF since the Oct 22
 * call: the dashboard's Getting started section replaces it (the video, the
 * tour and the three promises, at the top and replayable), and the region
 * question lives in Settings → Region. The sheet's code is all still there:
 * the header `?` button keeps opening it in help mode, and the `?welcome=1`
 * and `?tour=` hooks keep working. Flipping this back to `true` restores the
 * once-per-account popup exactly as before.
 */
export const WELCOME_POPUP_ENABLED: boolean = false;
